import { NgModule } from '@angular/core';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { RoutesModule } from './routes.module';
import { SharedModule } from './shared/shared.module';
import { FormlyConfigModule } from './configuration/angular/formly-config.module';
import { NgxPermissionsModule } from 'ngx-permissions';
import { ToastrModule } from 'ngx-toastr';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { DefaultLayoutComponent } from '../app/layout/default-layout/default-layout.component';
import { SidebarComponent } from '../app/layout/sidebar/sidebar.component';
import { AccordionDirective } from '../app/layout/sidebar/accordion.directive';
import { AccordionItemDirective } from '../app/layout/sidebar/accordionItem.directive';
import { AccordionAnchorDirective } from '../app/layout/sidebar/accordionanchor.directive';
import { NavbarComponent } from '../app/layout/navbar/navbar.component';
import { FooterComponent } from '../app/layout/footer/footer.component';
import { TranslateComponent } from '../app/layout/widgets/translate.component';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { AppTimeoutComponent, TimeoutDialog } from './layout/widgets/timeout.component';

import { environment } from '@env/environment';
import { BASE_URL } from '@core/interceptors/base-url-interceptor';
import { httpInterceptorProviders } from '@core/interceptors';
import { appInitializerProviders } from '@core/initializers';

import { IPublicClientApplication, PublicClientApplication, InteractionType, LogLevel, BrowserCacheLocation } from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule, MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent
} from '@azure/msal-angular';

import { b2cPolicies, protectedResources } from '../app/auth/auth-config';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { ComplaintsComponent } from './components/complaints/complaints.component';
import { ComplaintSubmissionDialogComponent } from './components/complaints/complaint-submission-dialog/complaint-submission-dialog.component';
import { StaffComponent } from './components/common/staff/staff.component';
import { AddressComponent } from './components/common/address/address.component';
import { ContactComponent } from './components/common/contact/contact.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { StaffAddEditComponent } from './components/common/staff/add-edit/staff-add-edit.component';
import { ApplicationsComponent } from './components/common/applications/applications.component';
import { ConfirmationDialogueComponent } from './components/common/confirmation-dialogue/confirmation-dialogue.component';
import { PaymentsComponent } from './components/payments/components/payments/payments.component';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { PaymentsModule } from './components/payments/payment.module';
import { AdminAccountsComponent } from './components/common/admin-accounts/admin-accounts.component';
import { AddressAddEditComponent } from './components/common/address/add-edit/address-add-edit.component';
import { AtpComponent } from './components/atp/atp.component';
import { AtpEditComponent } from './components/atp/atp-edit/atp-edit.component';
import { AtpApplicationComponent } from './components/atp/atp-application/atp-application.component';
import { AtpEditCourseComponent } from './components/atp/atp-edit-course/atp-edit-course.component';
import { AtpEditCourseStudentComponent } from './components/atp/atp-edit-course-student/atp-edit-course-student.component';
import { AtpCourseStudentUploadComponent } from './components/atp/atp-course-student-upload/atp-course-student-upload.component';
import { AtpEditStaffComponent } from './components/atp/atp-edit-staff/atp-edit-staff.component';
import { AtpApplicationListComponent } from './components/atp/atp-application-list/atp-application-list.component';
import { AtpEditCourseDocumentComponent } from './components/atp/atp-edit-course-document/atp-edit-course-document.component';
import { FirmManagementComponent } from './components/firms/firm-management/firm-management.component';
import { FirmEditComponent } from './components/firms/firm-edit/firm-edit.component';
import { FirmEditAddressDialogComponent } from './components/firms/firm-edit/firm-edit-address-dialog.component';
import { FirmEditContactInformationDialogComponent } from './components/firms/firm-edit/firm-edit-contact-information-dialog.component';
import { FirmEditCredentialDialogComponent } from './components/firms/firm-edit/firm-edit-credential-dialog.component';
import { FirmApplicationListComponent } from './components/firms/firm-application-list/firm-application-list.component';
import { FirmApplicationComponent } from './components/firms/firm-application/firm-application.component';
import { FirmApplicationPersonDialogComponent } from './components/firms/firm-application-person-dialog/firm-application-person-dialog.component';
import { IndividualListComponent } from './components/individuals/individual-list/individual-list.component';
import { IndividualApplicationListComponent } from './components/individuals/individual-application-list/individual-application-list.component';
import { IndividualApplicationComponent } from './components/individuals/individual-application/individual-application.component';
import { IndividualEditComponent } from './components/individuals/individual-edit/individual-edit.component';
import { IndividualAddAffiliationDialogComponent } from './components/individuals/individual-edit/individual-add-affiliation-dialog.component';
import { IndividualEditAddressDialogComponent } from './components/individuals/individual-edit/individual-edit-address-dialog.component';
import { IndividualEditContactInformationDialogComponent } from './components/individuals/individual-edit/individual-edit-contact-information-dialog.component';
import { AccountLinkApprovalComponent } from './components/common/account-link-approval/account-link-approval.component';
import { StaffAffiliationRequestDialogComponent } from './components/common/staff-affiliation-request-dialog/staff-affiliation-request-dialog.component';
import { DocumentUploadDialogComponent } from './components/common/document-upload-dialog/document-upload-dialog.component';
import SwiperCore, { A11y, Navigation } from 'swiper';
import { SwiperModule } from 'swiper/angular';

const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

SwiperCore.use([Navigation, A11y]);

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.b2c.clientId,
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri: environment.b2c.redirectUri,
      postLogoutRedirectUri: environment.b2c.postLogoutRedirectUri,
      knownAuthorities: [b2cPolicies.authorityDomain]
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE, // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Warning,
        piiLoggingEnabled: true
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(protectedResources.endpoint, protectedResources.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...protectedResources.scopes]
    },
    loginFailedRoute: 'login-failed'
  };
}

// Required for AOT compilation
export function TranslateHttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    DefaultLayoutComponent,
    DashboardComponent,
    TimeoutDialog,
    AppTimeoutComponent,
    SidebarComponent,
    AccordionDirective,
    AccordionItemDirective,
    AccordionAnchorDirective,
    NavbarComponent,
    FooterComponent,
    TranslateComponent,
    ConfirmDialogComponent,
    ComplaintsComponent,
    ComplaintSubmissionDialogComponent,
    StaffComponent,
    AddressComponent,
    ContactComponent,
    StaffAddEditComponent,
    ApplicationsComponent,
    ConfirmationDialogueComponent,
    AdminAccountsComponent,
    AddressAddEditComponent,
    AtpComponent,
    AtpEditComponent,
    AtpApplicationComponent,
    AtpEditCourseComponent,
    AtpEditCourseStudentComponent,
    AtpCourseStudentUploadComponent,
    AtpEditStaffComponent,
    AtpApplicationListComponent,
    AtpEditCourseDocumentComponent,
    FirmManagementComponent,
    FirmEditComponent,
    FirmEditAddressDialogComponent,
    FirmEditContactInformationDialogComponent,
    FirmEditCredentialDialogComponent,
    FirmApplicationListComponent,
    FirmApplicationComponent,
    FirmApplicationPersonDialogComponent,
    IndividualListComponent,
    IndividualApplicationListComponent,
    IndividualApplicationComponent,
    IndividualEditComponent,
    IndividualAddAffiliationDialogComponent,
    IndividualEditAddressDialogComponent,
    IndividualEditContactInformationDialogComponent,
    AccountLinkApprovalComponent,
    StaffAffiliationRequestDialogComponent,
    DocumentUploadDialogComponent,
  ],
  imports: [
    BrowserModule,
    PaymentsModule,
    NgIdleKeepaliveModule.forRoot(),
    BrowserAnimationsModule,
    HttpClientModule,
    SwiperModule,
    RoutesModule,
    SharedModule,
    MsalModule,
    FormlyConfigModule.forRoot(),
    NgxPermissionsModule.forRoot(),
    ToastrModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: TranslateHttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    PDFExportModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: BASE_URL, useValue: environment.baseUrl },
    httpInterceptorProviders,
    appInitializerProviders
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
